import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  message: ReactNode
  variant: ToastVariant
  duration?: number
}

type ToastContextValue = {
  toasts: Toast[]
  success: (message: ReactNode, duration?: number) => string
  error: (message: ReactNode, duration?: number) => string
  info: (message: ReactNode, duration?: number) => string
  warning: (message: ReactNode, duration?: number) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef<Map<string, number>>(new Map())

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
    const timeoutId = timers.current.get(id)
    if (timeoutId != null) {
      clearTimeout(timeoutId)
      timers.current.delete(id)
    }
  }, [])

  const enqueue = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).slice(2)
      const nextToast: Toast = { ...toast, id }
      setToasts((current) => [...current, nextToast])

      const duration = toast.duration ?? 3000
      if (duration > 0) {
        const timeoutId = window.setTimeout(() => {
          dismiss(id)
        }, duration)
        timers.current.set(id, timeoutId)
      }

      return id
    },
    [dismiss]
  )

  useEffect(() => {
    return () => {
      timers.current.forEach((timeoutId) => clearTimeout(timeoutId))
      timers.current.clear()
    }
  }, [])

  const success = useCallback(
    (message: ReactNode, duration?: number) => enqueue({ message, duration, variant: 'success' }),
    [enqueue]
  )
  const error = useCallback(
    (message: ReactNode, duration?: number) => enqueue({ message, duration, variant: 'error' }),
    [enqueue]
  )
  const info = useCallback(
    (message: ReactNode, duration?: number) => enqueue({ message, duration, variant: 'info' }),
    [enqueue]
  )
  const warning = useCallback(
    (message: ReactNode, duration?: number) => enqueue({ message, duration, variant: 'warning' }),
    [enqueue]
  )

  const contextValue = useMemo<ToastContextValue>(
    () => ({ toasts, dismiss, success, error, info, warning }),
    [toasts, dismiss, success, error, info, warning]
  )

  return <ToastContext.Provider value={contextValue}>{children}</ToastContext.Provider>
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

